import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { WeightInput, formatWeight } from '../components/WeightInput.js'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { useNav } from '../nav/NavContext.js'

interface TripRow {
  id: string
  trip_no: string
  booking_no: string
  customer_name: string
  origin: string
  destination: string
  status: string
  registration_no: string | null
  driver_name: string | null
}

interface BookingItem {
  id: string
  consignor: string | null
  consignee: string | null
  material: string | null
  weight: string | null
}

/** Consignment details typed at booking time, so they are not typed twice on the LR. */
interface LrPrefill {
  consignor: string
  consignee: string
  material: string
  weight: string
}

function prefillFrom(item: BookingItem | undefined): LrPrefill | undefined {
  if (!item) return undefined
  return {
    consignor: item.consignor ?? '',
    consignee: item.consignee ?? '',
    material: item.material ?? '',
    weight: item.weight ?? '',
  }
}

interface LrSummary {
  id: string
  booking_item_id: string | null
  lr_no: string
  consignor: string
  consignee: string
  total_amount: string
  amendment_count: number
}

interface TripExpenseRow {
  id: string
  entry_type: 'Withdrawal' | 'Expense'
  expense_head_name: string | null
  amount: string
  entry_date: string
  remarks: string | null
}

interface TripProfitability {
  revenue: string
  revenue_is_actual: boolean
  total_expense: string
  net_profit: string
  margin_pct: string | null
  is_final: boolean
}

interface EwayBillData {
  id: string
  eway_bill_no: string
  generated_date: string
  expiry_datetime: string
  origin_pincode: string | null
  destination_pincode: string | null
  approx_distance_km: string | null
  expected_transit_hours: string | null
  distance_overridden: boolean
  transit_hours_overridden: boolean
  status: 'Active' | 'Expired' | 'Fulfilled'
}

interface PodData {
  id: string
  delivery_date: string
  received_by_name: string | null
  pod_document_url: string | null
  remarks: string | null
}

interface TripDetailData {
  id: string
  trip_no: string
  booking_id: string
  booking_no: string
  booking_type: 'FTL' | 'PTL'
  origin: string
  destination: string
  customer_name: string
  registration_no: string | null
  driver_name: string | null
  status: string
  booking_items: BookingItem[]
  lorry_receipts: LrSummary[]
  status_history: { to_status: string; changed_at: string }[]
  dispatch: { dispatch_datetime: string; odometer_reading: string | null } | null
  eway_bill: EwayBillData | null
  pod: PodData | null
}

const TRANSIT_STEPS = ['Planned', 'Assigned', 'Dispatched', 'InTransit', 'Delivered', 'PODReceived']

/**
 * Stage 5 §4: the Trip Register + Trip Detail screen — the natural home for
 * everything Stage 5 adds (LR tab, dispatch, transit progression) and what
 * later stages (POD, invoicing) will keep attaching to (Stage 5 §4.2).
 */
export function TripsPage() {
  const [trips, setTrips] = useState<TripRow[]>([])
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const query = new URLSearchParams({ pageSize: '50' })
      if (statusFilter) query.set('trip_status', statusFilter)
      const result = await api.get<{ rows: TripRow[] }>(`/trips?${query.toString()}`)
      setTrips(result.rows)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div>
      <div className="tf-toolbar">
        <h2 style={{ margin: 0 }}>Trips</h2>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="tf-branch-select">
          <option value="">All statuses</option>
          {TRANSIT_STEPS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="tf-error">{error}</p>}

      <table className="tf-table">
        <thead>
          <tr>
            <th>Trip No.</th>
            <th>Booking</th>
            <th>Customer</th>
            <th>Route</th>
            <th>Vehicle / Driver</th>
            <th>Status</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={7}>Loading…</td>
            </tr>
          )}
          {!loading && trips.length === 0 && (
            <tr>
              <td colSpan={7}>No records found</td>
            </tr>
          )}
          {!loading &&
            trips.map((t) => (
              <tr key={t.id}>
                <td>{t.trip_no}</td>
                <td>{t.booking_no}</td>
                <td>{t.customer_name}</td>
                <td>
                  {t.origin} → {t.destination}
                </td>
                <td>
                  {t.registration_no ?? '—'}
                  {t.driver_name ? ` · ${t.driver_name}` : ''}
                </td>
                <td>{t.status}</td>
                <td className="tf-row-actions">
                  <button type="button" onClick={() => setDetailId(t.id)}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {detailId && (
        <TripDetail
          tripId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  )
}

function TripDetail({ tripId, onClose, onChanged }: { tripId: string; onClose: () => void; onChanged: () => void }) {
  const { can } = useAuth()
  const { go } = useNav()
  const [trip, setTrip] = useState<TripDetailData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lrFormOpen, setLrFormOpen] = useState<{ mode: 'create' | 'amend'; bookingItemId?: string; lr?: LrSummary; prefill?: LrPrefill | undefined } | null>(null)
  const [printLrId, setPrintLrId] = useState<string | null>(null)
  const [dispatchFormOpen, setDispatchFormOpen] = useState(false)
  const [ewayFormOpen, setEwayFormOpen] = useState(false)
  const [podFormOpen, setPodFormOpen] = useState(false)
  const [expenses, setExpenses] = useState<TripExpenseRow[]>([])
  const [profitability, setProfitability] = useState<TripProfitability | null>(null)

  const reload = useCallback(async () => {
    try {
      const data = await api.get<TripDetailData>(`/trips/${tripId}`)
      setTrip(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load trip')
    }
  }, [tripId])

  const reloadExpenses = useCallback(async () => {
    // Stage 6 §4.2: same petty_cash_entries data as the Petty Cash register,
    // filtered to this trip — read-only here, logged from the Petty Cash page.
    const rows = await api.get<TripExpenseRow[]>(`/trips/${tripId}/expenses`)
    setExpenses(rows)
  }, [tripId])

  // Stage 10 §2.3: a trip_profitability view row, live — never blocked just
  // because something (e.g. brokerage) is still Pending; shown as
  // Provisional until invoiced and fully settled (is_final).
  const reloadProfitability = useCallback(async () => {
    setProfitability(await api.get<TripProfitability>(`/trips/${tripId}/profitability`))
  }, [tripId])

  useEffect(() => {
    void reload()
    void reloadExpenses()
    void reloadProfitability()
  }, [reload, reloadExpenses, reloadProfitability])

  const uncoveredItems = trip?.booking_items.filter(
    (item) => !trip.lorry_receipts.some((lr) => lr.booking_item_id === item.id),
  )
  const canGenerateLr =
    trip?.status === 'Assigned' &&
    (trip.booking_type === 'FTL'
      ? trip.lorry_receipts.length === 0
      : (uncoveredItems?.length ?? 0) > 0)
  const canDispatch = trip?.status === 'Assigned' && (trip.lorry_receipts.length ?? 0) > 0

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover tf-slideover-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Trip {trip?.trip_no}</h2>
        {!trip && <p className="tf-note">Loading…</p>}
        {trip && (
          <>
            <p className="tf-note">
              <strong>{trip.customer_name}</strong> · {trip.origin} → {trip.destination}
              <br />
              {trip.registration_no ?? 'Outsourced vehicle'} {trip.driver_name ? `· ${trip.driver_name}` : ''}
            </p>

            <ol className="tf-timeline">
              {TRANSIT_STEPS.map((step) => (
                <li key={step} data-done={trip.status_history.some((h) => h.to_status === step)}>
                  {step}
                </li>
              ))}
            </ol>

            <h2>Lorry Receipts</h2>
            {trip.lorry_receipts.length === 0 && <p className="tf-note">None generated yet.</p>}
            {trip.lorry_receipts.map((lr) => (
              <div key={lr.id} className="tf-card" style={{ marginTop: '0.5rem' }}>
                <p className="tf-note">
                  <strong>{lr.lr_no}</strong> — {lr.consignor} → {lr.consignee} — ₹{lr.total_amount}
                  {lr.amendment_count > 0 && ` (amended ${lr.amendment_count}×)`}
                </p>
                <div className="tf-row-actions">
                  <button type="button" onClick={() => setPrintLrId(lr.id)}>
                    Print
                  </button>
                  <button type="button" onClick={() => setLrFormOpen({ mode: 'amend', lr })}>
                    Amend
                  </button>
                </div>
              </div>
            ))}

            {canGenerateLr && (
              <div className="tf-form-actions tf-no-print" style={{ marginTop: '0.75rem' }}>
                {trip.booking_type === 'FTL' ? (
                  <button type="button" className="tf-button-primary" onClick={() => setLrFormOpen({ mode: 'create', prefill: prefillFrom(trip.booking_items[0]) })}>
                    + Generate LR
                  </button>
                ) : (
                  uncoveredItems?.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="tf-button-primary"
                      onClick={() => setLrFormOpen({ mode: 'create', bookingItemId: item.id, prefill: prefillFrom(item) })}
                    >
                      + LR for {item.consignee ?? item.id}
                    </button>
                  ))
                )}
              </div>
            )}

            <h2 style={{ marginTop: '1.5rem' }}>Dispatch &amp; Transit</h2>
            {trip.dispatch && (
              <p className="tf-note">
                Dispatched {new Date(trip.dispatch.dispatch_datetime).toLocaleString()}
                {trip.dispatch.odometer_reading && ` · odometer ${trip.dispatch.odometer_reading}`}
              </p>
            )}
            {error && <p className="tf-error">{error}</p>}
            <div className="tf-row-actions">
              {canDispatch && (
                <button type="button" className="tf-button-primary" onClick={() => setDispatchFormOpen(true)}>
                  Dispatch
                </button>
              )}
              {trip.status === 'Dispatched' && (
                <TransitButton
                  label="Mark In Transit"
                  busy={busy}
                  setBusy={setBusy}
                  setError={setError}
                  tripId={tripId}
                  status="InTransit"
                  onDone={async () => {
                    await reload()
                    onChanged()
                  }}
                />
              )}
              {trip.status === 'InTransit' && (
                <TransitButton
                  label="Mark Delivered"
                  busy={busy}
                  setBusy={setBusy}
                  setError={setError}
                  tripId={tripId}
                  status="Delivered"
                  onDone={async () => {
                    await reload()
                    onChanged()
                  }}
                />
              )}
            </div>

            <h2 style={{ marginTop: '1.5rem' }}>E-Way Bill</h2>
            {trip.eway_bill ? (
              <div className="tf-card">
                <p className="tf-note">
                  <strong>{trip.eway_bill.eway_bill_no}</strong> — status: {trip.eway_bill.status}
                  <br />
                  {trip.eway_bill.approx_distance_km
                    ? `~${trip.eway_bill.approx_distance_km} km, ${trip.eway_bill.expected_transit_hours}h transit`
                    : 'Distance not estimated — pincode not recognised'}
                  {trip.eway_bill.distance_overridden && ' (distance overridden)'}
                  <br />
                  Expires {new Date(trip.eway_bill.expiry_datetime).toLocaleString()}
                </p>
              </div>
            ) : (
              trip.lorry_receipts.length > 0 && (
                <div className="tf-form-actions tf-no-print">
                  <button type="button" className="tf-button-primary" onClick={() => setEwayFormOpen(true)}>
                    + Enter E-Way Bill
                  </button>
                </div>
              )
            )}

            <h2 style={{ marginTop: '1.5rem' }}>Proof of Delivery</h2>
            {trip.pod ? (
              <div className="tf-card">
                <p className="tf-note">
                  Delivered {new Date(trip.pod.delivery_date).toLocaleDateString()}
                  {trip.pod.received_by_name && ` — received by ${trip.pod.received_by_name}`}
                  {trip.pod.remarks && <><br />Remarks: {trip.pod.remarks}</>}
                </p>
                {trip.pod.pod_document_url && (
                  <a href={trip.pod.pod_document_url} target="_blank" rel="noreferrer">
                    View document
                  </a>
                )}
              </div>
            ) : (
              trip.status === 'Delivered' && (
                <div className="tf-form-actions tf-no-print">
                  <button type="button" className="tf-button-primary" onClick={() => setPodFormOpen(true)}>
                    + Capture POD
                  </button>
                </div>
              )
            )}

            <h2 style={{ marginTop: '1.5rem' }}>Expenses</h2>
            {expenses.length === 0 && <p className="tf-note">None logged yet — log trip expenses from the Petty Cash page.</p>}
            {expenses.map((exp) => (
              <p key={exp.id} className="tf-note">
                {new Date(exp.entry_date).toLocaleDateString()} — {exp.expense_head_name ?? exp.entry_type} — ₹{exp.amount}
                {exp.remarks && ` (${exp.remarks})`}
              </p>
            ))}

            {profitability && (
              <>
                <h2 style={{ marginTop: '1.5rem' }}>
                  Profitability{' '}
                  <span className="tf-note">({profitability.is_final ? 'Final' : 'Provisional'})</span>
                </h2>
                <p>
                  Revenue: ₹{profitability.revenue} {!profitability.revenue_is_actual && '(estimated)'}
                  <br />
                  Total Expense: ₹{profitability.total_expense}
                  <br />
                  <strong>Net Profit: ₹{profitability.net_profit}</strong>
                  {profitability.margin_pct !== null && ` (${profitability.margin_pct}% margin)`}
                </p>
              </>
            )}
          </>
        )}

        <div className="tf-form-actions" style={{ marginTop: '1.5rem' }}>
          {can('audit_log.read') && (
            <button type="button" onClick={() => go('audit', { table: 'trips', record: tripId })}>
              View History
            </button>
          )}
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {lrFormOpen && trip && (
        <LrFormModal
          tripId={tripId}
          mode={lrFormOpen.mode}
          bookingItemId={lrFormOpen.bookingItemId}
          existing={lrFormOpen.lr}
          prefill={lrFormOpen.prefill}
          onClose={() => setLrFormOpen(null)}
          onSaved={async () => {
            setLrFormOpen(null)
            await reload()
            onChanged()
          }}
        />
      )}

      {printLrId && <LrPrintModal lrId={printLrId} onClose={() => setPrintLrId(null)} />}

      {dispatchFormOpen && (
        <DispatchFormModal
          tripId={tripId}
          onClose={() => setDispatchFormOpen(false)}
          onSaved={async () => {
            setDispatchFormOpen(false)
            await reload()
            onChanged()
          }}
        />
      )}

      {ewayFormOpen && (
        <EwayBillFormModal
          tripId={tripId}
          onClose={() => setEwayFormOpen(false)}
          onSaved={async () => {
            setEwayFormOpen(false)
            await reload()
            onChanged()
          }}
        />
      )}

      {podFormOpen && trip && (
        <PodFormModal
          tripId={tripId}
          registrationNo={trip.registration_no}
          onClose={() => setPodFormOpen(false)}
          onSaved={async () => {
            setPodFormOpen(false)
            await reload()
            onChanged()
          }}
        />
      )}
    </div>
  )
}

function TransitButton({
  label,
  status,
  tripId,
  busy,
  setBusy,
  setError,
  onDone,
}: {
  label: string
  status: 'InTransit' | 'Delivered'
  tripId: string
  busy: boolean
  setBusy: (b: boolean) => void
  setError: (e: string | null) => void
  onDone: () => Promise<void>
}) {
  async function click() {
    setBusy(true)
    setError(null)
    try {
      await api.patch(`/trips/${tripId}/status`, { status })
      await onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update status')
    } finally {
      setBusy(false)
    }
  }
  return (
    <button type="button" className="tf-button-primary" disabled={busy} onClick={() => void click()}>
      {label}
    </button>
  )
}

// ---------------------------------------------------------------------------
function DispatchFormModal({ tripId, onClose, onSaved }: { tripId: string; onClose: () => void; onSaved: () => void }) {
  const [odometer, setOdometer] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.post(`/trips/${tripId}/dispatch`, odometer ? { odometer_reading: odometer } : {})
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Dispatch failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Dispatch Trip</h2>
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>Odometer reading (optional)</span>
            <input type="number" value={odometer} onChange={(e) => setOdometer(e.target.value)} />
          </label>
          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Confirm Dispatch'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


// ---------------------------------------------------------------------------
// Stage 7 §2: pincode-driven distance/transit auto-calc, always shown
// editable rather than locked — the two number fields start blank and only
// show the auto-estimate once the API returns it, but can be typed over.
// ---------------------------------------------------------------------------
function EwayBillFormModal({ tripId, onClose, onSaved }: { tripId: string; onClose: () => void; onSaved: () => void }) {
  const [ewayBillNo, setEwayBillNo] = useState('')
  const [generatedDate, setGeneratedDate] = useState(() => new Date().toISOString().slice(0, 16))
  const [originPincode, setOriginPincode] = useState('')
  const [destinationPincode, setDestinationPincode] = useState('')
  const [distanceKm, setDistanceKm] = useState('')
  const [transitHours, setTransitHours] = useState('')
  const [expiry, setExpiry] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.post(`/trips/${tripId}/eway-bill`, {
        eway_bill_no: ewayBillNo,
        generated_date: new Date(generatedDate).toISOString(),
        origin_pincode: originPincode || undefined,
        destination_pincode: destinationPincode || undefined,
        approx_distance_km: distanceKm || undefined,
        expected_transit_hours: transitHours || undefined,
        expiry_datetime: expiry ? new Date(expiry).toISOString() : undefined,
      })
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save E-Way Bill')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Enter E-Way Bill</h2>
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>E-Way Bill No.</span>
            <input value={ewayBillNo} onChange={(e) => setEwayBillNo(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Generated date &amp; time</span>
            <input
              type="datetime-local"
              value={generatedDate}
              onChange={(e) => setGeneratedDate(e.target.value)}
              required
            />
          </label>
          <label className="tf-field">
            <span>Origin pincode</span>
            <input
              value={originPincode}
              onChange={(e) => setOriginPincode(e.target.value)}
              maxLength={6}
              placeholder="641001"
            />
          </label>
          <label className="tf-field">
            <span>Destination pincode</span>
            <input
              value={destinationPincode}
              onChange={(e) => setDestinationPincode(e.target.value)}
              maxLength={6}
              placeholder="600001"
            />
          </label>
          <label className="tf-field">
            <span>Distance (km) — leave blank to auto-estimate from pincodes</span>
            <input type="number" value={distanceKm} onChange={(e) => setDistanceKm(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Expected transit (hours) — leave blank to auto-estimate</span>
            <input type="number" value={transitHours} onChange={(e) => setTransitHours(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Expiry date &amp; time — leave blank to estimate from transit hours</span>
            <input type="datetime-local" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </label>
          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save E-Way Bill'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Stage 7 §3: POD capture. "Flag this vehicle" is opt-in — it never fires off
// the remarks text alone, since the person recording it decides whether the
// vehicle/driver is actually at fault (§3.1).
// ---------------------------------------------------------------------------
function PodFormModal({
  tripId,
  registrationNo,
  onClose,
  onSaved,
}: {
  tripId: string
  registrationNo: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const [deliveryDate, setDeliveryDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [receivedBy, setReceivedBy] = useState('')
  const [remarks, setRemarks] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [flagVehicle, setFlagVehicle] = useState(false)
  const [incidentType, setIncidentType] = useState<'Shortage' | 'Damage' | 'Other'>('Shortage')
  const [incidentDescription, setIncidentDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!file) {
      setError('A photo or PDF of the acknowledgement copy is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const form = new FormData()
      form.set('delivery_date', deliveryDate)
      if (receivedBy) form.set('received_by_name', receivedBy)
      if (remarks) form.set('remarks', remarks)
      form.set('document', file)
      if (flagVehicle) {
        form.set('flag_vehicle', 'true')
        form.set('incident_type', incidentType)
        if (incidentDescription) form.set('incident_description', incidentDescription)
      }
      await api.upload(`/trips/${tripId}/pod`, form)
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save POD')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Capture POD</h2>
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>Delivery date</span>
            <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Received by (optional)</span>
            <input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Acknowledgement copy (photo or PDF)</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              required
            />
          </label>
          <label className="tf-field">
            <span>Remarks — shortage/damage, if any (optional)</span>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </label>

          <label className="tf-field tf-field-checkbox">
            <input type="checkbox" checked={flagVehicle} onChange={(e) => setFlagVehicle(e.target.checked)} />
            <span>Flag this vehicle {registrationNo ? `(${registrationNo})` : ''} for future reference</span>
          </label>
          {flagVehicle && (
            <>
              <label className="tf-field">
                <span>Incident type</span>
                <select value={incidentType} onChange={(e) => setIncidentType(e.target.value as typeof incidentType)}>
                  <option value="Shortage">Shortage</option>
                  <option value="Damage">Damage</option>
                  <option value="Other">Other</option>
                </select>
              </label>
              <label className="tf-field">
                <span>What happened (optional)</span>
                <textarea value={incidentDescription} onChange={(e) => setIncidentDescription(e.target.value)} />
              </label>
            </>
          )}

          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save POD'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
interface ChargeType {
  id: string
  name: string
  is_default_on_new_lr: boolean
}
interface ChargeLineInput {
  charge_type_id: string
  amount: string
}

function LrFormModal({
  tripId,
  mode,
  bookingItemId,
  existing,
  prefill,
  onClose,
  onSaved,
}: {
  tripId: string
  mode: 'create' | 'amend'
  bookingItemId?: string | undefined
  existing?: LrSummary | undefined
  prefill?: LrPrefill | undefined
  onClose: () => void
  onSaved: () => void
}) {
  const [chargeTypes, setChargeTypes] = useState<ChargeType[]>([])
  const [consignor, setConsignor] = useState(existing?.consignor ?? prefill?.consignor ?? '')
  const [consignee, setConsignee] = useState(existing?.consignee ?? prefill?.consignee ?? '')
  const [consignorGstin, setConsignorGstin] = useState('')
  const [consigneeGstin, setConsigneeGstin] = useState('')
  const [articlesCount, setArticlesCount] = useState('')
  const [materialDescription, setMaterialDescription] = useState(prefill?.material ?? '')
  const [valueOfGoods, setValueOfGoods] = useState('')
  const [actualWeight, setActualWeight] = useState(prefill?.weight ?? '')
  const [chargedWeight, setChargedWeight] = useState('')
  const [freightTerm, setFreightTerm] = useState<'Paid' | 'ToPay' | 'TBB'>('ToPay')
  const [payMode, setPayMode] = useState('')
  const [gstPayableBy, setGstPayableBy] = useState<'Consignor' | 'Consignee' | 'Transporter' | ''>('')
  const [charges, setCharges] = useState<ChargeLineInput[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void api.get<{ rows: ChargeType[] }>('/charge-types?pageSize=100').then((res) => {
      setChargeTypes(res.rows)
      if (mode === 'create') {
        // Stage 5 §2.1: charge lines picked from the tenant's configured
        // charge_types, with the is_default_on_new_lr one pre-filled.
        const defaults = res.rows.filter((c) => c.is_default_on_new_lr)
        setCharges(defaults.map((c) => ({ charge_type_id: c.id, amount: '' })))
      }
    })
  }, [mode])

  function addChargeLine() {
    setCharges((prev) => [...prev, { charge_type_id: chargeTypes[0]?.id ?? '', amount: '' }])
  }
  function updateCharge(i: number, patch: Partial<ChargeLineInput>) {
    setCharges((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)))
  }
  function removeCharge(i: number) {
    setCharges((prev) => prev.filter((_, idx) => idx !== i))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        consignor,
        consignee,
        consignor_gstin: consignorGstin || undefined,
        consignee_gstin: consigneeGstin || undefined,
        articles_count: articlesCount || undefined,
        material_description: materialDescription || undefined,
        value_of_goods: valueOfGoods || undefined,
        actual_weight: actualWeight || undefined,
        charged_weight: chargedWeight || undefined,
        freight_term: freightTerm,
        pay_mode: payMode || undefined,
        gst_payable_by: gstPayableBy || undefined,
        charges: charges
          .filter((c) => c.charge_type_id && c.amount !== '')
          .map((c) => ({ charge_type_id: c.charge_type_id, amount: c.amount })),
      }
      if (mode === 'create') {
        if (bookingItemId) body.booking_item_id = bookingItemId
        await api.post(`/trips/${tripId}/lorry-receipts`, body)
      } else {
        await api.post(`/lorry-receipts/${existing!.id}/amend`, body)
      }
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover tf-slideover-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{mode === 'create' ? 'Generate' : 'Amend'} Lorry Receipt</h2>
        {mode === 'amend' && (
          <p className="tf-note">Amending is logged (who/when) — core fields are otherwise locked once saved.</p>
        )}
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>Consignor</span>
            <input value={consignor} onChange={(e) => setConsignor(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Consignor GSTIN</span>
            <input value={consignorGstin} onChange={(e) => setConsignorGstin(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Consignee</span>
            <input value={consignee} onChange={(e) => setConsignee(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Consignee GSTIN</span>
            <input value={consigneeGstin} onChange={(e) => setConsigneeGstin(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Articles count</span>
            <input type="number" value={articlesCount} onChange={(e) => setArticlesCount(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Material description</span>
            <input value={materialDescription} onChange={(e) => setMaterialDescription(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Value of goods (₹)</span>
            <input type="number" value={valueOfGoods} onChange={(e) => setValueOfGoods(e.target.value)} />
          </label>
          <WeightInput label="Actual weight" valueKg={actualWeight} onChangeKg={setActualWeight} />
          <WeightInput label="Charged weight" valueKg={chargedWeight} onChangeKg={setChargedWeight} />
          <label className="tf-field">
            <span>Freight term</span>
            <select value={freightTerm} onChange={(e) => setFreightTerm(e.target.value as typeof freightTerm)}>
              <option value="Paid">Paid</option>
              <option value="ToPay">To Pay</option>
              <option value="TBB">To Be Billed</option>
            </select>
          </label>
          <label className="tf-field">
            <span>Pay mode</span>
            <select value={payMode} onChange={(e) => setPayMode(e.target.value)}>
              <option value="">—</option>
              <option value="Cash">Cash</option>
              <option value="Bank">Bank</option>
              <option value="Credit">Credit</option>
            </select>
          </label>
          <label className="tf-field">
            <span>GST payable by</span>
            <select value={gstPayableBy} onChange={(e) => setGstPayableBy(e.target.value as typeof gstPayableBy)} required>
              <option value="" disabled>
                Select…
              </option>
              <option value="Consignor">Consignor</option>
              <option value="Consignee">Consignee</option>
              <option value="Transporter">Transporter</option>
            </select>
          </label>

          <div className="tf-field">
            <span>Charges</span>
            {charges.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                <select value={c.charge_type_id} onChange={(e) => updateCharge(i, { charge_type_id: e.target.value })}>
                  {chargeTypes.map((ct) => (
                    <option key={ct.id} value={ct.id}>
                      {ct.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Amount"
                  value={c.amount}
                  onChange={(e) => updateCharge(i, { amount: e.target.value })}
                />
                <button type="button" onClick={() => removeCharge(i)}>
                  ✕
                </button>
              </div>
            ))}
            <button type="button" onClick={addChargeLine}>
              + Add charge line
            </button>
          </div>

          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
interface PrintData {
  lorry_receipt: {
    lr_no: string
    lr_date: string
    consignor: string
    consignor_gstin: string | null
    consignee: string
    consignee_gstin: string | null
    articles_count: number | null
    material_description: string | null
    value_of_goods: string | null
    actual_weight: string | null
    charged_weight: string | null
    freight_term: string
    gst_payable_by: string
    total_amount: string
    booking_no: string
    origin: string
    destination: string
    registration_no: string | null
    charges: { charge_type_name: string; amount: string }[]
  }
  tenant: { company_name: string; gstin: string | null; bank_details: Record<string, string> | null; letterhead_phones: string[] }
  branch: { name: string; address: string | null; state: string | null; gstin: string | null }
}

const PRINT_COPIES = ['Consignor Copy', 'Consignee Copy', 'Driver Copy', 'Office Copy']

export function LrPrintModal({ lrId, onClose }: { lrId: string; onClose: () => void }) {
  const [data, setData] = useState<PrintData | null>(null)

  useEffect(() => {
    void api.get<PrintData>(`/lorry-receipts/${lrId}/print`).then(setData)
  }, [lrId])

  if (!data) {
    return (
      <div className="tf-slideover-backdrop" onClick={onClose}>
        <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
          <p className="tf-note">Loading…</p>
        </div>
      </div>
    )
  }

  const lr = data.lorry_receipt

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-print-sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '50rem', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="tf-no-print tf-form-actions" style={{ marginBottom: '1rem' }}>
          <button type="button" className="tf-button-primary" onClick={() => window.print()}>
            Print
          </button>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {/* Stage 5 §2.4: same content, four copy labels. */}
        {PRINT_COPIES.map((copy) => (
          <div key={copy} className="tf-print-copy">
            <div className="tf-print-copy-label">{copy}</div>
            <div className="tf-print-header">
              <div>
                <h1>{data.tenant.company_name}</h1>
                <div className="tf-note">
                  {data.branch.name}
                  {data.branch.address ? `, ${data.branch.address}` : ''}
                  {data.branch.state ? `, ${data.branch.state}` : ''}
                </div>
                {data.tenant.letterhead_phones.length > 0 && (
                  <div className="tf-note">Ph: {data.tenant.letterhead_phones.join(', ')}</div>
                )}
              </div>
              <div className="tf-note" style={{ textAlign: 'right' }}>
                <strong>LR No: {lr.lr_no}</strong>
                <br />
                Date: {new Date(lr.lr_date).toLocaleDateString()}
                <br />
                Vehicle: {lr.registration_no ?? 'Outsourced'}
              </div>
            </div>

            <div className="tf-print-grid">
              <div>
                <strong>Consignor:</strong> {lr.consignor}
                {lr.consignor_gstin && <> (GSTIN: {lr.consignor_gstin})</>}
              </div>
              <div>
                <strong>Consignee:</strong> {lr.consignee}
                {lr.consignee_gstin && <> (GSTIN: {lr.consignee_gstin})</>}
              </div>
              <div>
                <strong>Route:</strong> {lr.origin} → {lr.destination}
              </div>
              <div>
                <strong>Booking:</strong> {lr.booking_no}
              </div>
              <div>
                <strong>Material:</strong> {lr.material_description ?? '—'}
              </div>
              <div>
                <strong>Articles:</strong> {lr.articles_count ?? '—'}
              </div>
              <div>
                <strong>Actual / Charged weight:</strong> {formatWeight(lr.actual_weight)} / {formatWeight(lr.charged_weight)}
              </div>
              <div>
                <strong>Value of goods:</strong> ₹{lr.value_of_goods ?? '—'}
              </div>
              <div>
                <strong>Freight term:</strong> {lr.freight_term}
              </div>
              <div>
                <strong>GST payable by:</strong> {lr.gst_payable_by}
              </div>
            </div>

            <table className="tf-print-charges">
              <thead>
                <tr>
                  <th>Charge</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lr.charges.map((c, i) => (
                  <tr key={i}>
                    <td>{c.charge_type_name}</td>
                    <td>₹{c.amount}</td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>₹{lr.total_amount}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {lr.gst_payable_by !== 'Transporter' && (
              <p className="tf-note" style={{ marginTop: '0.5rem' }}>
                GST payable under RCM by {lr.gst_payable_by} directly to Government.
              </p>
            )}

            <p className="tf-note" style={{ marginTop: '1rem' }}>
              Received the consignment in good condition. Signature &amp; seal: ______________________
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
